import React from "react";
import { createPortal } from "react-dom";
import { Box, Typography, Breadcrumbs, Link } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useNavigate } from "react-router-dom";
import { useShell, usePageMeta } from "../layout/ShellContext";

/**
 * Standard screen header.
 *
 * On desktop it renders a title block with optional breadcrumbs and actions.
 * On mobile the title moves into the fixed top bar (so the header doesn't
 * consume a second row of vertical space) and only the actions stay inline.
 */
export default function PageHeader({
  title,
  subtitle,
  actions,
  mobileActions,
  breadcrumbs,
  backTo,
}) {
  const navigate = useNavigate();
  const { isMobile, actionsSlot } = useShell();

  usePageMeta({ title, backTo });

  if (isMobile) {
    // The title already lives in the fixed top bar. Only a compact icon-style
    // control passed as `mobileActions` is portalled up beside it — regular
    // page actions stay in the flow, where there's room for them.
    const portalled =
      mobileActions && actionsSlot ? createPortal(mobileActions, actionsSlot) : null;

    if (!subtitle && !actions) return portalled;

    return (
      <>
        {portalled}
        <Box sx={{ mb: 1.5 }}>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: actions ? 1.5 : 0 }}>
              {subtitle}
            </Typography>
          )}
          {actions && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", "& > *": { minWidth: 0 } }}>
              {actions}
            </Box>
          )}
        </Box>
      </>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      {breadcrumbs?.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}
          sx={{ mb: 1, "& .MuiBreadcrumbs-li": { fontSize: "0.8125rem" } }}
        >
          {breadcrumbs.map((crumb, i) =>
            crumb.to && i < breadcrumbs.length - 1 ? (
              <Link
                key={crumb.label}
                component="button"
                underline="hover"
                color="text.secondary"
                onClick={() => navigate(crumb.to)}
                sx={{ fontSize: "0.8125rem", background: "none", border: 0, cursor: "pointer" }}
              >
                {crumb.label}
              </Link>
            ) : (
              <Typography key={crumb.label} variant="body2" color="text.primary">
                {crumb.label}
              </Typography>
            )
          )}
        </Breadcrumbs>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h2">{title}</Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexShrink: 0 }}>
            {actions}
          </Box>
        )}
      </Box>
    </Box>
  );
}
